import { Card, Checkbox, Flex, SimpleTable, Typography } from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../../components/ExpandableCard";
import { GithubTypesLink } from "../../urls";

const { Title, Text } = Typography;

const CheckboxPage = () => {
  const [isChecked, setIsChecked] = useState(false);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Checkbox</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Checkbox component in Djuno Design is a customizable input
            element for selecting one or more options from a set. It provides a
            clear visual indication of its state (checked or unchecked), and it
            supports various customization options, including tooltips, error
            states, and labels.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Checkbox component offers several customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Label with Tooltip
                </Text>
                : The Checkbox can include a label alongside a tooltip for
                additional context or explanations.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Error Handling
                </Text>
                : When provided with an error prop, the Checkbox visually
                indicates an error state, making it clear to users that a
                correction is needed.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Required Indicator
                </Text>
                : A required indicator (asterisk) is displayed when the Checkbox
                is marked as required, helping users identify mandatory fields.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Disabled State
                </Text>
                : The Checkbox can be disabled, preventing any interaction and
                visually indicating its disabled state.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              codeViewerProps={{
                code: CheckboxExample1,
              }}
            >
              <Flex direction="col" className="gap-5">
                <Flex className="gap-5 w-full">
                  <Flex direction="col">
                    <Checkbox
                      label="Djuno Design"
                      value={isChecked}
                      onChange={setIsChecked}
                    />
                  </Flex>
                </Flex>

                <Flex items="center" className="gap-5 w-full">
                  <Checkbox
                    label="Djuno Design"
                    value={isChecked}
                    onChange={setIsChecked}
                    disabled
                  />
                </Flex>
                <Flex items="center" className="gap-5 w-full">
                  <Checkbox
                    label="is required?"
                    value={isChecked}
                    onChange={setIsChecked}
                    required
                  />
                </Flex>
                <Flex items="center" className="gap-5 w-full">
                  <Checkbox
                    label="Djuno Design"
                    value={isChecked}
                    onChange={setIsChecked}
                    tooltip={{ content: "it's a tooltip" }}
                  />
                </Flex>
                <Flex items="center" className="gap-5 w-full">
                  <Checkbox
                    label="Djuno Design"
                    value={isChecked}
                    onChange={setIsChecked}
                    error="error"
                  />
                </Flex>
                <Flex items="center" className="gap-5 w-full">
                  <Checkbox
                    label="Djuno Design"
                    value={isChecked}
                    onChange={setIsChecked}
                    error
                  />
                </Flex>
                <Flex items="center" className="gap-5 w-full">
                  <Checkbox
                    label="Ckeckbox with custom label"
                    value={isChecked}
                    onChange={setIsChecked}
                    labelClassName="text-green-500 font-bold"
                  />
                </Flex>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* id */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">id</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Unique identifier</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* label */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">label</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The label displayed next to the checkbox.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* value */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">value</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls whether the checkbox is checked or unchecked.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onChange */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onChange</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback function triggered when the checkbox state
                        changes.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`(checked: boolean)=>void`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* disabled */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">disabled</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Disables the checkbox, preventing user interaction and
                        indicating the disabled state visually.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* required */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">required</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Indicates whether the checkbox is required. Displays an
                        asterisk if true.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* error */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">error</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        An error message to display below the checkbox,
                        indicating a validation issue.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | boolean | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tooltip */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tooltip</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A tooltip providing additional information, displayed
                        when hovering over the checkbox label.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L37"}
                        size="xs"
                        code
                      >
                        TooltipProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* labelClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">labelClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes to apply custom styles to the
                        input's label.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const CheckboxExample1 = `<Flex direction="col" className="gap-5">
  <Flex className="gap-5 w-full">
    <Flex direction="col">
      <Checkbox
        label="Djuno Design"
        value={isChecked}
        onChange={setIsChecked}
      />
    </Flex>
  </Flex>

  <Flex items="center" className="gap-5 w-full">
    <Checkbox
      label="Djuno Design"
      value={isChecked}
      onChange={setIsChecked}
      disabled
    />
  </Flex>
  <Flex items="center" className="gap-5 w-full">
    <Checkbox
      label="is required?"
      value={isChecked}
      onChange={setIsChecked}
      required
    />
  </Flex>
  <Flex items="center" className="gap-5 w-full">
    <Checkbox
      label="Djuno Design"
      value={isChecked}
      onChange={setIsChecked}
      tooltip={{ content: "it's a tooltip" }}
    />
  </Flex>
  <Flex items="center" className="gap-5 w-full">
    <Checkbox
      label="Djuno Design"
      value={isChecked}
      onChange={setIsChecked}
      error="error"
    />
  </Flex>
  <Flex items="center" className="gap-5 w-full">
    <Checkbox
      label="Djuno Design"
      value={isChecked}
      onChange={setIsChecked}
      error
    />
  </Flex>
  <Flex items="center" className="gap-5 w-full">
    <Checkbox
      label="Ckeckbox with custom label"
      value={isChecked}
      onChange={setIsChecked}
      labelClassName="text-green-500 font-bold"
    />
  </Flex>
</Flex>`;

export default CheckboxPage;
